import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import { lighten, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { MobileSubmenuPanel } from './mobile-submenu-panel';
import type { SidebarItem } from './types';
import { useSidebarData } from './use-sidebar-data';

interface MobileNavDialogProps {
  open: boolean;
  onClose: () => void;
}

interface NavTileProps {
  item: SidebarItem;
  onClose: () => void;
  onOpenSubmenu: (item: SidebarItem) => void;
}

const NavTile = ({ item, onClose, onOpenSubmenu }: NavTileProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isActive = item.path ? location.pathname === item.path : false;
  const Icon = item.icon;
  const hasChildren = item.children && item.children.length > 0;

  const handleClick = () => {
    // Community is the only entry with more than one destination a user is
    // likely to need mid-session (several spaces), so it alone opens a
    // second-layer chooser instead of guessing which space to land on.
    if (hasChildren && item.id === 'community') {
      onOpenSubmenu(item);
      return;
    }
    if (hasChildren && item.path) {
      navigate({ to: item.path });
    }
    if (item.onClick) {
      item.onClick();
    }
    onClose();
  };

  const tile = (
    <Box
      onClick={hasChildren || item.onClick ? handleClick : onClose}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.75,
        py: 1.25,
        px: 2,
        borderRadius: 2,
        cursor: 'pointer',
        flex: 1,
        color: isActive ? 'primary.main' : theme.palette.grey[500],
        transition: 'color 0.15s ease',
        '&:active': {
          bgcolor: lighten(theme.palette.primary.main, 0.92),
        },
      }}
    >
      <Icon size={24} width="24px" height="24px" />
      <Typography variant="caption" fontWeight={400} fontSize="9px" lineHeight={1} sx={{ color: 'inherit' }}>
        {item.label}
      </Typography>
    </Box>
  );

  if (item.path && !hasChildren) {
    return (
      <Link to={item.path} style={{ textDecoration: 'none', color: 'inherit', flex: 1, display: 'flex' }}>
        {tile}
      </Link>
    );
  }

  return tile;
};

export const MobileNavDialog = ({ open, onClose }: MobileNavDialogProps) => {
  const { sidebarData } = useSidebarData();
  const [submenuItem, setSubmenuItem] = useState<SidebarItem | null>(null);

  // Start back at the tile row next time the sheet opens, rather than
  // reopening on whichever submenu was last shown.
  useEffect(() => {
    if (!open) {
      setSubmenuItem(null);
    }
  }, [open]);

  const allItems = sidebarData
    .filter(section => !section.desktopOnly)
    .flatMap(section => section.items)
    .filter(item => item.path || item.children?.length || item.onClick);

  const handleClose = () => {
    setSubmenuItem(null);
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      anchor="bottom"
      slotProps={{ backdrop: { invisible: true } }}
      PaperProps={{
        sx: {
          width: 'auto',
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          px: 2,
          pt: 1,
          pb: 'env(safe-area-inset-bottom, 16px)',
          boxShadow: '0 -4px 24px rgba(0,0,0,0.08)',
          ...(submenuItem && { height: '75vh' }),
        },
      }}
    >
      {/* Drag handle */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
        <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: 'grey.300' }} />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-around', pb: 1 }}>
        {allItems.map(item => (
          <NavTile key={item.id} item={item} onClose={handleClose} onOpenSubmenu={setSubmenuItem} />
        ))}
      </Box>

      {submenuItem && (
        <MobileSubmenuPanel item={submenuItem} onBack={() => setSubmenuItem(null)} onClose={handleClose} />
      )}
    </Drawer>
  );
};
